import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import { useCreateGroup } from '../../providers/CreateGroupProvider';
import { usePermissions } from '../../hooks/usePermissions';

interface RebootSchedulerProps {
  setBarCheck: (value: boolean) => void;
}

const daysOfWeek = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const daysMap: { [key: string]: string } = {
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
};

export type TimeConversion = {
  time: string; // 'HH:mm'
  dayOffset: number; // -1, 0, or 1 relative to the reference day
};

// Converts a wall-clock 'HH:mm' string between an IANA timezone and UTC (or vice versa).
export const convertTime = (time: string, tz: string, direction: 'toUTC' | 'toLocal'): TimeConversion | null => {
  if (!time || !tz) return null;
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  // Use a fixed reference date so DST offsets are resolved consistently.
  const referenceDate = '2024-01-01';

  const source =
    direction === 'toUTC'
      ? moment.tz(`${referenceDate} ${time}`, 'YYYY-MM-DD HH:mm', tz)
      : moment.utc(`${referenceDate} ${time}`, 'YYYY-MM-DD HH:mm');

  const converted = direction === 'toUTC' ? source.clone().utc() : source.clone().tz(tz);

  // Compare calendar dates (not instants) to detect a day rollover from the conversion.
  const dayOffset = moment(converted.format('YYYY-MM-DD')).diff(moment(source.format('YYYY-MM-DD')), 'days');

  return { time: converted.format('HH:mm'), dayOffset };
};

export const localToUTCTime = (time: string, tz: string) => convertTime(time, tz, 'toUTC');
export const utcToLocalTime = (time: string, tz: string) => convertTime(time, tz, 'toLocal');

export const formatDayOffset = (dayOffset: number) => {
  if (dayOffset > 0) return ` (+${dayOffset} day${dayOffset > 1 ? 's' : ''})`;
  if (dayOffset < 0) return ` (${dayOffset} day${dayOffset < -1 ? 's' : ''})`;
  return '';
};

const RebootScheduler: React.FC<RebootSchedulerProps> = ({ setBarCheck }) => {
  const [rebootSection, setRebootSection] = useState<boolean>(false);
  const navigate = useNavigate();
  const { CreateGroupData, setCreateGroupData } = useCreateGroup();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('groupPolicies');
  const fieldsDisabled = rebootSection || readOnly;

  // Grab any existing maintenance schedule from context if available
  const existingSchedule = CreateGroupData?.groupMaintenanceSchedule?.[0];

  // The timezone selected earlier in the flow; times are always persisted in context as UTC.
  const selectedTimezone = (CreateGroupData as any)?.timezoneValue || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Setup form initial values using Formik, with fallback defaults
  const initialValues = useMemo(
    () => ({
      windowname: existingSchedule?.scheduleTitle || '',
      startTime: (existingSchedule?.timeWindowStart && utcToLocalTime(existingSchedule.timeWindowStart, selectedTimezone)?.time) || '',
      endTime: (existingSchedule?.timeWindowEnd && utcToLocalTime(existingSchedule.timeWindowEnd, selectedTimezone)?.time) || '',
      selectedDays:
        existingSchedule?.daysOfWeek?.map((day) => Object.keys(daysMap).find((key) => daysMap[key] === day))?.filter(Boolean) || [], // convert full days (e.g., Monday) to short codes (e.g., MON)
      delayReboot: '',
      forceReboot: '',
    }),
    [existingSchedule, selectedTimezone],
  );

  // Form validation rules
  const validationSchema = Yup.object().shape({
    windowname: Yup.string().required('Maintenance window name is required'),
    startTime: Yup.string().required('Start Time is required'),
    endTime: Yup.string().required('End Time is required'),
    selectedDays: Yup.array().min(1, 'Select at least one day'),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={readOnly ? undefined : validationSchema}
      enableReinitialize={true}
      onSubmit={(values) => {
        const createdDate = new Date(); // Now
        const tenYearsLater = new Date(createdDate);
        tenYearsLater.setFullYear(createdDate.getFullYear() + 10);

        // ✅ Convert selectedDays (short form) into full weekday names (long form)
        const fullDays = values.selectedDays.map((d) => daysMap[d]).filter(Boolean);

        // ✅ Times are entered in the group's selected timezone but persisted/sent to the API as UTC
        const utcStart = localToUTCTime(values.startTime, selectedTimezone);
        const utcEnd = localToUTCTime(values.endTime, selectedTimezone);

        // ✅ Every day of the week selected (Sun–Sat) means it recurs daily; anything less is a weekly recurrence
        const frequency = values.selectedDays.length === daysOfWeek.length ? 'Days' : 'Weeks';

        // ✅ Set context with updated group maintenance data
        setCreateGroupData((prev) => ({
          ...prev,
          groupMaintenanceSchedule: [
            {
              scheduleTitle: values.windowname,
              scheduleDescription: values.windowname,
              startDateTime: createdDate.toISOString(),
              endDateTime: tenYearsLater.toISOString(),
              frequency,
              interval: 1,
              timeWindowStart: utcStart?.time || values.startTime,
              timeWindowEnd: utcEnd?.time || values.endTime,
              daysOfWeek: fullDays, // ✅ API expects full names
              daysOfMonth: [],
              isRecurring: true,
              isActive: true,
              isDefault: true,
            },
          ],
        }));
        // ✅ Move to next screen
        setBarCheck(true);
        navigate('/groupPolicy/Reboot');
      }}
    >
      {({ values, setFieldValue }) => (
        <Form className="bg-[var(--Background-Color,#1D1D1D)] text-white p-6 rounded-lg space-y-6 flex-1">
          {/* Maintenance Window Name */}
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="block text-sm mb-1">Maintenance Window Name*</label>
              <Field name="windowname" disabled={readOnly} className="w-full bg-[#1e1e1e] text-white p-2 rounded border border-gray-600" />
              <ErrorMessage name="windowname" component="div" className="text-red-400 text-xs mt-1" />
            </div>
          </div>

          {/* Start and End Times */}
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="block text-sm mb-1">Start Time*</label>
              <Field
                type="time"
                name="startTime"
                disabled={fieldsDisabled}
                className="w-full bg-[#1e1e1e] text-white p-2 rounded border border-gray-600"
              />
              <ErrorMessage name="startTime" component="div" className="text-red-400 text-xs mt-1" />
              {values.startTime && (
                <div className="text-xs text-gray-400 mt-2">
                  {/*<span className="text-gray-300">{values.startTime}</span>*/}
                  {'UTC '}
                  <span className="text-white font-medium">
                    {localToUTCTime(values.startTime, selectedTimezone)?.time}
                    {formatDayOffset(localToUTCTime(values.startTime, selectedTimezone)?.dayOffset || 0)}
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1">
              <label className="block text-sm mb-1">End Time*</label>
              <Field
                type="time"
                name="endTime"
                disabled={fieldsDisabled}
                className="w-full bg-[#1e1e1e] text-white p-2 rounded border border-gray-600"
              />
              <ErrorMessage name="endTime" component="div" className="text-red-400 text-xs mt-1" />
              {values.endTime && (
                <div className="text-xs text-gray-400 mt-2">
                  {/*<span className="text-gray-300">{values.endTime}</span>*/}
                  {'UTC '}
                  <span className="text-white font-medium">
                    {localToUTCTime(values.endTime, selectedTimezone)?.time}
                    {formatDayOffset(localToUTCTime(values.endTime, selectedTimezone)?.dayOffset || 0)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Day Selection Buttons */}
          <div>
            <label className="block text-sm mb-1">Select Days*</label>
            <div className="flex space-x-2 mb-1">
              {daysOfWeek.map((day) => {
                const isSelected = values.selectedDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={fieldsDisabled}
                    onClick={() => {
                      const updatedDays = isSelected ? values.selectedDays.filter((d) => d !== day) : [...values.selectedDays, day];
                      setFieldValue('selectedDays', updatedDays);
                    }}
                    className={`px-4 py-2 rounded ${
                      isSelected ? 'bg-red-600' : 'bg-[#1e1e1e]'
                    } ${fieldsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <ErrorMessage name="selectedDays" component="div" className="text-red-400 text-xs mt-1" />
            {values.selectedDays.length > 0 && (
              <p className="text-xs text-gray-400">Occurs every {values.selectedDays.map((d) => daysMap[d]).join(', ')}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-start gap-4 mt-6">
            <button type="button" style={closeBtnStyle} onClick={() => navigate('/Group')}>
              Discard
            </button>
            <button type="button" style={closeBtnStyle} onClick={() => navigate('/groupPolicy/CreateGroup')}>
              Previous
            </button>
            <button type="submit" style={nextBtnStyle}>
              Next
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

// Button styles
const closeBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #555',
  borderRadius: '2rem',
  cursor: 'pointer',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

const nextBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#b31317',
  color: '#fff',
  border: 'none',
  borderRadius: '2rem',
  cursor: 'pointer',
  fontWeight: 'bold',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

export default RebootScheduler;
